import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { AlertTriangle, CreditCard, Mail, Phone, ChevronRight, Calendar, DollarSign } from 'lucide-react';
import Loader2 from '@/components/brand/Loader2';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AccountSuspended = () => {
  const { user, logout, refreshStatus } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    loadSubscription();
  }, []);

  const loadSubscription = async () => {
    try {
      const response = await axios.get(`${API}/billing/my-subscription`);
      setSubscription(response.data.subscription);
    } catch (error) {
      console.error('Error loading subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkStatus = async () => {
    setChecking(true);
    try {
      await refreshStatus();
      // If user is no longer suspended, redirect to app
      const userRes = await axios.get(`${API}/users/me`);
      if (userRes.data.subscription_status !== 'suspended' && !userRes.data.blocked_reason) {
        navigate('/app/dashboard');
      }
    } catch (error) {
      console.error('Error checking status:', error);
    } finally {
      setChecking(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4" data-testid="account-suspended">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="/brand/app-icon-square.webp"
            alt="Onor Code" 
            className="h-16 w-16 mx-auto rounded-lg mb-4"
          />
          <h1 className="font-teko text-3xl text-white">[ONOR] CODE</h1>
        </div>

        {/* Suspended Card */}
        <Card className="card-default border-amber-500/30">
          <CardContent className="p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-amber-500/20 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-amber-400" />
              </div>
              <h2 className="font-teko text-2xl text-white mb-2">CUENTA SUSPENDIDA</h2>
              <p className="text-zinc-400 text-sm">
                Tu suscripción ha vencido y tu cuenta está temporalmente suspendida.
              </p>
            </div>

            {/* Subscription Details */}
            {subscription && (
              <div className="bg-zinc-800/50 rounded-lg p-4 mb-6 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-400 text-sm">Plan</span>
                  <span className="text-white">{subscription.plan_name || 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-zinc-400 text-sm">Venció el</span>
                  <span className="text-amber-400">{subscription.current_period_end || 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-zinc-400 text-sm">Monto pendiente</span>
                  <span className="text-white">${subscription.amount_mxn || 0} MXN</span>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-3">
              <p className="text-zinc-300 text-sm text-center mb-4">
                Para reactivar tu cuenta, contacta a nuestro equipo o realiza el pago pendiente.
              </p>

              <a 
                href="mailto:soporte@onorcode.com"
                className="flex items-center justify-between p-4 bg-primary/10 border border-primary/30 rounded-lg hover:bg-primary/20 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-primary" />
                  <span className="text-white">Contactar por email</span>
                </div>
                <ChevronRight className="w-4 h-4 text-primary" />
              </a>

              <a 
                href="https://wa.me/521234567890"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg hover:bg-emerald-500/20 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-emerald-400" />
                  <span className="text-white">WhatsApp</span>
                </div>
                <ChevronRight className="w-4 h-4 text-emerald-400" />
              </a>
            </div>

            {/* Check Status Button */}
            <div className="mt-6 pt-6 border-t border-zinc-800">
              <Button 
                onClick={checkStatus}
                disabled={checking}
                className="w-full btn-secondary"
              >
                {checking ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verificando...
                  </>
                ) : (
                  'Ya realicé mi pago'
                )}
              </Button>
              <p className="text-zinc-500 text-xs text-center mt-2">
                Si ya realizaste tu pago, haz clic para verificar
              </p>
            </div>

            {/* Logout */}
            <div className="mt-4 text-center">
              <Button 
                variant="ghost"
                onClick={logout}
                className="text-zinc-400 hover:text-white text-sm"
              >
                Cerrar sesión
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Info */}
        <p className="text-zinc-500 text-xs text-center mt-6">
          Si crees que esto es un error, contacta a soporte inmediatamente.
        </p>
      </div>
    </div>
  );
};

export default AccountSuspended;
